import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Calendar,
  CheckSquare,
  Eye,
  EyeOff,
  Home,
  ListTodo,
  LogOut,
  MessageCircle,
  Receipt,
  User,
  Users,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import type { Event, Organization } from '@/lib/types';
import { HavaDurumu } from '@/components/ui/HavaDurumu';
import { NamazVakti } from '@/components/ui/NamazVakti';
import { cn } from '@/lib/utils';

const navItems = [
  { to: '/dashboard', icon: Home, label: 'Ana Sayfa' },
  { to: '/events', icon: Calendar, label: 'Program' },
  { to: '/tasks', icon: ListTodo, label: 'Görev' },
  { to: '/checklists', icon: CheckSquare, label: 'Liste' },
  { to: '/members', icon: Users, label: 'Kişiler' },
  { to: '/expenses', icon: Receipt, label: 'Harcama' },
  { to: '/chat', icon: MessageCircle, label: 'Sohbet' },
  { to: '/profile', icon: User, label: 'Profil' },
];

/**
 * Üst başlıkta hangi programın bilgisi gösterilecek: süren varsa o,
 * yoksa en yakın gelecek olan.
 *
 * Eksik veri burada elenmiyor; her gösterge kendi şartına bakıyor
 * (hava koordinat ister, namaz vakti ilçe kodu). Böylece koordinatı
 * girilmemiş ama ilçesi girilmiş bir programda vakitler yine görünüyor.
 */
function enYakinProgram(events?: Event[]): Event | undefined {
  if (!events?.length) return undefined;
  const bugun = new Date().toLocaleDateString('sv-SE');
  const tarihli = events.filter((e) => e.startDate);

  const suren = tarihli.find(
    (e) => e.startDate!.slice(0, 10) <= bugun && (e.endDate ?? e.startDate)!.slice(0, 10) >= bugun,
  );
  if (suren) return suren;

  return tarihli
    .filter((e) => e.startDate!.slice(0, 10) > bugun)
    .sort((a, b) => a.startDate!.localeCompare(b.startDate!))[0];
}

export function AppLayout() {
  const { user, logout, isSuperAdmin, viewAsUser, setViewAsUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isChat = location.pathname.startsWith('/chat');

  /* Üst başlıkta hangi programın havası gösterilecek: süren varsa o,
     yoksa en yakın gelecek program. Sorgular zaten önbellekte
     (organizations ve events başka sayfalarda da çekiliyor). */
  const { data: orgs } = useQuery({
    queryKey: ['organizations'],
    queryFn: () => api<{ organizations: Organization[] }>('/organizations'),
    enabled: !!user,
  });
  const orgId = orgs?.organizations[0]?.id;
  const { data: events } = useQuery({
    queryKey: ['events', orgId],
    queryFn: () => api<{ events: Event[] }>(`/events/org/${orgId}`),
    enabled: !!orgId,
  });
  const yakinProgram = enYakinProgram(events?.events);

  return (
    <div className="mx-auto flex min-h-dvh max-w-lg flex-col">
      <header className="book-cover-header sticky top-0 z-30 px-4 pb-4 pt-3 text-primary-foreground">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            {/* Marka işareti kütüphaneyle aynı: kitabın üstünde alev. */}
            <img src="/ikon/logo-nav.svg" alt="" aria-hidden="true" className="mt-0.5 h-11 w-auto" />
            <div>
            <p className="font-serif text-[11px] uppercase tracking-[0.25em] text-accent-light/90">
              Risale-i Nur İzinde
            </p>
            <h1 className="font-serif text-xl font-semibold tracking-wide">Program Yönetimi</h1>
            {user && (
              <p className="mt-1 text-xs text-primary-foreground/75">{user.fullName}</p>
            )}
            {/* Kampın yerindeki hava ve sıradaki namaz vakti. Hava:
                tarih uzaksa anlık, yaklaşınca o günün tahmini, kamp
                başlayınca yine anlık. */}
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-primary-foreground/85">
              <HavaDurumu event={yakinProgram} />
              <NamazVakti event={yakinProgram} />
            </div>
            </div>
          </div>
          <div className="flex items-start gap-2">
            {isSuperAdmin && (
              <button
                type="button"
                onClick={() => setViewAsUser(!viewAsUser)}
                className={`rounded-lg border px-2 py-1.5 text-[10px] font-medium transition-colors ${
                  viewAsUser
                    ? 'border-accent/50 bg-accent/20 text-accent-light'
                    : 'border-accent/30 text-accent-light hover:bg-white/10'
                }`}
                title={viewAsUser ? 'Admin görünümüne geç' : 'Normal kullanıcı gibi gör'}
              >
                {viewAsUser ? (
                  <span className="flex items-center gap-1">
                    <EyeOff className="h-3.5 w-3.5" />
                    Kullanıcı
                  </span>
                ) : (
                  <span className="flex items-center gap-1">
                    <Eye className="h-3.5 w-3.5" />
                    Admin
                  </span>
                )}
              </button>
            )}
            <button
              onClick={() => {
                logout();
                navigate('/login');
              }}
              className="rounded-lg border border-accent/30 p-2 text-accent-light hover:bg-white/10"
              aria-label="Çıkış"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="gold-divider mt-3 opacity-60" />
      </header>

      <main
        className={cn(
          'flex-1 overflow-y-auto px-4 pt-4',
          // Alt çubuğun ÜSTÜNDE bir de kütüphane şeridi var; içerik onun
          // altında kalmasın diye pay bir tık arttırıldı.
          isChat ? 'pb-40' : 'pb-[calc(var(--bottom-nav-height)+3rem+env(safe-area-inset-bottom))]',
        )}
      >
        <Outlet />
      </main>

      {/* Kardeş site — kütüphane. Ayrı bir uygulama olduğu için düz bağ.
          Sohbette gizli: mesaj yazma çubuğu da alt çubuğun hemen üstünde
          duruyor, ikisi aynı yeri paylaşınca şerit yazma alanını
          örtüyordu. Sohbetten çıkınca yine görünüyor. */}
      {!isChat && (
        <a
          href="https://risaletinnur.com"
          target="_blank"
          rel="noopener"
          className="fixed bottom-0 left-0 right-0 z-30 mx-auto flex max-w-lg items-center justify-center gap-1.5 border-t border-accent/25 bg-parchment-dark/95 py-1.5 text-[11px] text-muted-foreground backdrop-blur"
          style={{ bottom: 'calc(var(--bottom-nav-height) + env(safe-area-inset-bottom, 0px))' }}
        >
          <img src="/ikon/logo.svg" alt="" aria-hidden className="h-4 w-4 shrink-0" />
          <span>
            Risalet-in Nur Kütüphanesi<span className="text-accent"> · risaletinnur.com</span>
          </span>
        </a>
      )}

      <nav className="bottom-nav fixed bottom-0 left-0 right-0 z-40 mx-auto max-w-lg">
        <div className="flex gap-0.5 overflow-x-auto px-1 py-1.5 scrollbar-none">
          {navItems.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                cn(
                  'flex min-w-[4.25rem] shrink-0 flex-col items-center gap-0.5 rounded-lg px-2 py-1.5 text-[10px] font-medium transition-colors',
                  isActive
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:bg-accent/10 hover:text-primary',
                )
              }
            >
              <Icon className="h-5 w-5" />
              {label}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}

export function PublicLayout() {
  return (
    <div className="mx-auto flex min-h-dvh max-w-lg flex-col">
      <header className="book-cover-header px-4 pb-6 pt-5 text-primary-foreground">
        <img src="/ikon/logo-nav.svg" alt="" aria-hidden="true" className="mb-3 h-16 w-auto" />
        <p className="font-serif text-xs uppercase tracking-[0.3em] text-accent-light/90">
          Bediüzzaman Said Nursi İzinde
        </p>
        <h1 className="font-serif mt-2 text-3xl font-semibold leading-tight">
          Program Yönetimi
        </h1>
        <div className="gold-divider my-4 opacity-50" />
        <p className="text-sm leading-relaxed text-primary-foreground/85">
          Barla, Çamdağı ve maneviyat programlarınızı dijital ortamda yönetin.
        </p>
      </header>
      <main className="flex-1 p-4">
        <Outlet />
      </main>
      <footer className="px-4 pb-6 text-center">
        <a
          href="https://risaletinnur.com"
          target="_blank"
          rel="noopener"
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary"
        >
          <img src="/ikon/logo.svg" alt="" aria-hidden className="h-4 w-4 shrink-0" />
          Risalet-in Nur Kütüphanesi · risaletinnur.com
        </a>
      </footer>
    </div>
  );
}
